import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useNavConfig } from '../context/NavConfigContext';
import { useSecurityPin } from '../context/SecurityPinContext';
import { NAV_CATEGORIES, NAV_FUNCTIONS } from '../lib/navFunctions';
import { openCashDrawer } from '../lib/receipt';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Pin, PinOff, ArrowRight, Info, Banknote, ShieldCheck } from 'lucide-react';

export default function FunzioniPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, hasPerm } = useAuth();
  const { isPinned, pinFunction, unpinFunction } = useNavConfig();
  const { runSensitive } = useSecurityPin();
  const [sections, setSections] = useState(null);
  const [drawerBusy, setDrawerBusy] = useState(false);

  const isAdmin = user?.role === 'admin';
  const canOpenDrawer = user?.role === 'admin' || user?.role === 'manager';

  const handleOpenDrawer = async () => {
    if (drawerBusy) return;
    setDrawerBusy(true);
    try {
      await openCashDrawer(runSensitive);
      toast.success(t('funzioni.drawerOpened', { defaultValue: 'Cassetto aperto' }));
    } catch (e) {
      if (e?.message === 'cancelled') { /* PIN annullato dall'utente */ }
      else {
        const msg = e?.response?.data?.detail || e?.message || t('funzioni.drawerError', { defaultValue: 'Impossibile aprire il cassetto' });
        toast.error(msg);
      }
    } finally { setDrawerBusy(false); }
  };

  useEffect(() => {
    api.get('/sections').then(({ data }) => {
      const map = {};
      (data.sections || []).forEach((s) => { map[s.key] = s; });
      setSections(map);
    }).catch(() => setSections({}));
  }, []);

  const isVisible = useMemo(() => (item) => {
    if (!hasPerm(item.perm)) return false;
    if (!sections) return true;
    const sec = sections[item.perm];
    if (!sec) return true;
    if (!sec.enabled) return false;
    return (sec.roles || []).includes(user?.role);
  }, [hasPerm, sections, user]);

  const togglePin = async (fn) => {
    try {
      if (isPinned(fn.key)) { await unpinFunction(fn.key); toast.success(t('funzioni.movedToPage', { name: t(fn.tKey) })); }
      else { await pinFunction(fn.key); toast.success(t('funzioni.movedToSidebar', { name: t(fn.tKey) })); }
    } catch (e) {
      toast.error(t('funzioni.saveError'));
    }
  };

  // Build category groups of visible functions. Admin sees all; others see only
  // the functions NOT pinned to the sidebar (the overflow they need here).
  const groups = NAV_CATEGORIES.map((cat) => {
    const items = NAV_FUNCTIONS.filter((f) => f.cat === cat.key && isVisible(f) && (isAdmin || !isPinned(f.key)));
    return { ...cat, items };
  }).filter((g) => g.items.length > 0);

  const totalVisible = groups.reduce((n, g) => n + g.items.length, 0);

  return (
    <div className="px-4 md:px-6 lg:px-8 py-5 md:py-6 max-w-6xl">
      {isAdmin && (
        <div className="mt-4 mb-2 flex items-start gap-2.5 rounded-xl border border-border bg-muted/50 p-3 text-sm text-muted-foreground" data-testid="funzioni-admin-hint">
          <Info className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
          <span>{t('funzioni.adminHint')}</span>
        </div>
      )}

      {canOpenDrawer && (
        <section className="mt-5" data-testid="funzioni-cassa-section">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2.5">
            {t('funzioni.catCassa', { defaultValue: 'Cassa' })}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            <Card
              data-testid="funzioni-open-drawer-card"
              className="group relative p-4 flex flex-col gap-3 cursor-pointer hover:border-primary/40 hover:shadow-md transition-[box-shadow,border-color] focus-within:ring-2 focus-within:ring-ring"
              role="button"
              tabIndex={0}
              onClick={handleOpenDrawer}
              onKeyDown={(e) => { if (e.key === 'Enter') handleOpenDrawer(); }}
              aria-disabled={drawerBusy}
            >
              <div className="flex items-start justify-between">
                <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                  <Banknote className="h-5 w-5" />
                </div>
                <Badge variant="secondary" className="gap-1 text-[10px]"><ShieldCheck className="h-3 w-3" /> PIN</Badge>
              </div>
              <div className="min-w-0">
                <div className="font-display font-bold leading-tight truncate">{t('funzioni.openDrawer', { defaultValue: 'Apri cassetto' })}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {drawerBusy ? t('funzioni.drawerOpening', { defaultValue: 'Apertura…' }) : t('funzioni.openDrawerDesc', { defaultValue: 'Apri il cassetto portasoldi' })}
                </div>
              </div>
            </Card>
          </div>
        </section>
      )}

      {totalVisible === 0 ? (
        <Card className="mt-6 p-10 text-center text-muted-foreground" data-testid="funzioni-empty">
          {t('funzioni.empty')}
        </Card>
      ) : (
        <div className="mt-5 space-y-7">
          {groups.map((cat) => (
            <section key={cat.key} data-testid={`funzioni-cat-${cat.key}`}>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2.5">{t(cat.sKey)}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {cat.items.map((fn) => {
                  const Icon = fn.icon;
                  const pinned = isPinned(fn.key);
                  return (
                    <Card
                      key={fn.key}
                      data-testid={`funzioni-card-${fn.key}`}
                      className="group relative p-4 flex flex-col gap-3 cursor-pointer hover:border-primary/40 hover:shadow-md transition-[box-shadow,border-color] focus-within:ring-2 focus-within:ring-ring"
                      onClick={() => navigate(fn.path)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') navigate(fn.path); }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                          <Icon className="h-5 w-5" />
                        </div>
                        {isAdmin && pinned && (
                          <Badge variant="secondary" className="gap-1 text-[10px]"><Pin className="h-3 w-3" /> {t('funzioni.inSidebar')}</Badge>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="font-display font-bold leading-tight truncate">{t(fn.tKey)}</div>
                        <div className="mt-0.5 inline-flex items-center gap-1 text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                          {t('funzioni.open')} <ArrowRight className="h-3 w-3" />
                        </div>
                      </div>

                      {isAdmin && (
                        <Button
                          variant={pinned ? 'outline' : 'default'}
                          size="sm"
                          className="mt-1 w-full rounded-lg"
                          data-testid={`funzioni-toggle-${fn.key}`}
                          onClick={(e) => { e.stopPropagation(); togglePin(fn); }}
                        >
                          {pinned
                            ? (<><PinOff className="h-4 w-4 mr-1.5" /> {t('funzioni.removeFromSidebar')}</>)
                            : (<><Pin className="h-4 w-4 mr-1.5" /> {t('funzioni.addToSidebar')}</>)}
                        </Button>
                      )}
                    </Card>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
